import { useEffect, useState } from "react";

import { Cookie } from "lucide-react";

import { Button } from "@rook/shared/components/ui/button";

/**
 * GDPR consent banner. Sticky to the bottom of the viewport on
 * first visit; once the customer makes a choice the banner stays
 * dismissed for that browser via localStorage. No tracking is
 * loaded before the customer accepts — the gating happens at the
 * read site (analytics shippers + the error reporter both
 * check `hasAnalyticsConsent()` before sending).
 *
 * Three-state model:
 *   - "unset"     → banner visible, no analytics fire.
 *   - "accepted"  → banner hidden, analytics + error reporter active.
 *   - "rejected"  → banner hidden, analytics off, error reporter
 *                   still on (the error reporter is operational
 *                   reliability, not analytics; customers can't
 *                   opt out of "the portal didn't crash silently").
 *
 * The dismiss state lives in localStorage under a versioned key so
 * a future policy change can re-prompt by bumping the version.
 *
 * Mirrors the design from foundations/PENDING-WORK.md → "GDPR
 * consent banner — informational". Closes that pending item.
 */

const STORAGE_KEY = "rook-portal:consent:v1";

type Consent = "accepted" | "rejected";

const readConsent = (): Consent | null => {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === "accepted" || raw === "rejected") return raw;
  return null;
};

const writeConsent = (value: Consent): void => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, value);
};

/** Read-only consent check for tracking code in other modules. */
export const hasAnalyticsConsent = (): boolean =>
  readConsent() === "accepted";

export const ConsentBanner = () => {
  // Render-gated by client-side state because SSR isn't on the
  // critical path for Vite, but defending against the SSR scenario
  // anyway: localStorage read is gated behind `typeof window`.
  const [consent, setConsent] = useState<Consent | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setConsent(readConsent());
    setMounted(true);
  }, []);

  const onAccept = () => {
    writeConsent("accepted");
    setConsent("accepted");
  };
  const onReject = () => {
    writeConsent("rejected");
    setConsent("rejected");
  };

  if (!mounted || consent !== null) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie + analytics consent"
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-2xl rounded-xl border border-white/10 bg-card/95 px-4 py-3.5 shadow-xl backdrop-blur-md sm:inset-x-auto sm:right-3"
    >
      <div className="flex items-start gap-3">
        <Cookie
          className="mt-0.5 h-5 w-5 shrink-0 text-amber-400"
          aria-hidden
        />
        <div className="flex-1 space-y-1">
          <p className="text-sm font-medium text-foreground">
            We&apos;d like to keep an eye on portal usage.
          </p>
          <p className="text-xs text-muted-foreground">
            ROOK uses first-party analytics to understand which
            portal features clients actually use. Operational
            telemetry (error reports, audit logs) runs regardless —
            consent only controls product analytics.
          </p>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onReject}>
          Reject
        </Button>
        <Button size="sm" onClick={onAccept}>
          Accept
        </Button>
      </div>
    </div>
  );
};
